/**
 * What: Defines the authenticated Shopify bootstrap boundary for browser API calls.
 * Transport & data-flow: Reads verified /api/me identity and checks every bootstrap response against it.
 *
 */
import { shopifyApiFetch, ShopifyBootstrapScopeError, type ShopifyAppBridge } from './shopifyApiFetch'
import {
  readShopifySessionIdentity,
  sameShopifySessionIdentity,
  shopifySessionIdentitySchema,
} from '@testfeed/shopify-contracts'
import { waitForShopifyOperation } from './shopifyRequestLifecycle'
import { markShopifyStartupPhase } from './startupTiming'

const INVALID_SHOP_VALUES = new Set(['no-shop', 'unknown-shop'])

export { ShopifyBootstrapScopeError } from './shopifyApiFetch'

/** Normalizes an App Bridge shop value and rejects placeholders used by fail-closed UI state. */
export function normalizeShopifyShop(shop: string | null | undefined): string | null {
  const normalized = shop?.trim()
  if (!normalized || INVALID_SHOP_VALUES.has(normalized)) return null
  return normalized
}

/** Reads the Shopify-owned lifecycle scope without consulting browser URL parameters. */
export function getAppBridgeShop(shopify: ShopifyAppBridge): string | null {
  return normalizeShopifyShop(shopify.config?.shop)
}

/**
 * Capture a verified principal, allowing native token rotation while refusing mixed shop/user/session batches.
 */
export async function captureAuthenticatedShopBootstrap(
  shopify: ShopifyAppBridge,
  options: { expectedShop?: string | null; signal?: AbortSignal } = {},
) {
  markShopifyStartupPhase('bridgeWaitStartedAt')
  await waitForShopifyOperation(options.signal, async () => shopify.ready)
  markShopifyStartupPhase('bridgeReadyAt')
  const shopDomain = getAppBridgeShop(shopify)
  if (!shopDomain) {
    throw new ShopifyBootstrapScopeError('Shopify shop scope is unavailable.')
  }

  if (options.expectedShop && shopDomain !== options.expectedShop)
    throw new ShopifyBootstrapScopeError('Shopify shop scope changed during bootstrap.')
  const response = await shopifyApiFetch(shopify, '/api/me', { init: { signal: options.signal } })
  if (!response.ok) throw new Error(`Me API Error: ${response.status}`)
  markShopifyStartupPhase('bootstrapParseStartedAt')
  const data: unknown = await waitForShopifyOperation(options.signal, () => response.json())
  const sessionIdentity = shopifySessionIdentitySchema.parse(
    typeof data === 'object' && data && 'sessionIdentity' in data ? data.sessionIdentity : undefined,
  )
  if (
    sessionIdentity.shopDomain !== shopDomain ||
    !sameShopifySessionIdentity(sessionIdentity, readShopifySessionIdentity(response.headers))
  )
    throw new ShopifyBootstrapScopeError('Authenticated Shopify session does not match App Bridge shop scope.')
  markShopifyStartupPhase('bootstrapParsedAt')
  const authenticatedShopify: ShopifyAppBridge = {
    ready: shopify.ready,
    config: { shop: shopDomain },
    sessionIdentity,
  }
  return { shopDomain, authenticatedShopify }
}
